<template>
  <div class="material-name-cell" @click.stop>
    <template v-if="editing">
      <el-input
        ref="inputRef"
        v-model="draft"
        size="small"
        class="material-name-cell__input"
        maxlength="128"
        :disabled="submitting"
        @keyup.enter="commit"
        @keyup.esc="cancel"
        @blur="onBlur"
      />
    </template>
    <template v-else>
      <span class="material-name-cell__text" :title="displayName">{{ displayName }}</span>
      <el-button
        v-if="editable"
        link
        type="primary"
        class="material-name-cell__edit"
        @mousedown.prevent
        @click.stop="startEdit"
      >
        <el-icon><EditPen /></el-icon>
      </el-button>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { EditPen } from '@element-plus/icons-vue'

const props = defineProps({
  row: { type: Object, required: true },
  editable: { type: Boolean, default: false },
  designId: { type: [Number, String, null], default: null },
})

const emit = defineEmits(['success'])

const editing = ref(false)
const draft = ref('')
const submitting = ref(false)
const inputRef = ref(null)

const resolveFileName = (item) => {
  const name = String(item?.fileName || '').trim()
  if (name) return name
  const path = String(item?.savePath || '')
  return path.split('/').pop() || '未命名'
}

const resolveFileExtension = (item) => {
  const path = String(item?.savePath || '')
  const fromPath = path.match(/(\.[a-zA-Z0-9]+)$/)?.[1]
  if (fromPath) return fromPath.toLowerCase()
  const fromName = String(item?.fileName || '').match(/(\.[a-zA-Z0-9]+)$/)?.[1]
  if (fromName) return fromName.toLowerCase()
  const type = String(item?.type || '').toLowerCase()
  if (type === 'image/jpeg') return '.jpg'
  if (type === 'image/png') return '.png'
  if (type === 'image/gif') return '.gif'
  if (type === 'image/webp') return '.webp'
  if (type === 'image/bmp') return '.bmp'
  if (type === 'image/svg+xml') return '.svg'
  return ''
}

/** 保留真实文件后缀，避免改显示名影响下载/预览 */
const normalizeRenamedFileName = (item, rawName) => {
  const ext = resolveFileExtension(item)
  const trimmed = String(rawName || '').trim()
  if (!ext) return trimmed
  const extLower = ext.toLowerCase()
  if (!trimmed.toLowerCase().endsWith(extLower)) {
    const base = trimmed.replace(/\.[^./\\]+$/, '') || trimmed
    return `${base}${ext}`
  }
  return trimmed
}

const displayName = computed(() => {
  if (props.row?.rowType === 'folder') return props.row.folder?.label || ''
  return resolveFileName(props.row.item)
})

const startEdit = async () => {
  if (!props.editable) return
  draft.value = displayName.value
  editing.value = true
  await nextTick()
  inputRef.value?.focus?.()
  inputRef.value?.select?.()
}

const cancel = () => {
  editing.value = false
  draft.value = ''
}

const onBlur = () => {
  if (!editing.value || submitting.value) return
  commit()
}

const commit = async () => {
  if (!editing.value || submitting.value) return
  const name = String(draft.value || '').trim()
  if (!name) {
    ElMessage.warning('名称不能为空')
    return
  }
  if (name === displayName.value) {
    cancel()
    return
  }
  submitting.value = true
  try {
    if (props.row.rowType === 'folder') {
      const folderId = props.row.folder?.folderId
      if (!folderId) return
      await axios.post('/api/image-libraries/folders/rename', {
        id: folderId,
        designId: props.designId,
        name,
      })
    } else {
      const item = props.row.item
      if (!item?.id) return
      const finalName = normalizeRenamedFileName(item, name)
      if (finalName !== name) {
        ElMessage.info(`已自动保留原文件后缀 ${resolveFileExtension(item)}`)
      }
      await axios.post('/api/image-libraries/submit', {
        id: item.id,
        fileName: finalName,
        category: item.category || '',
        tag: item.tag || '',
        isPrivate: item.isPrivate === 1 ? 1 : 0,
        savePath: item.savePath || '',
        fileSize: item.fileSize || 0,
        type: item.type || '',
      })
    }
    ElMessage.success('重命名成功')
    emit('success')
    cancel()
  } catch (e) {
    ElMessage.error(e?.response?.data?.msg || '重命名失败')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.material-name-cell {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  gap: 2px;
  min-height: 24px;
}
.material-name-cell__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}
.material-name-cell__edit {
  flex-shrink: 0;
  padding: 0 2px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease;
}
.material-name-cell:hover .material-name-cell__edit {
  opacity: 1;
  visibility: visible;
}
.material-name-cell__input {
  width: 100%;
}
</style>
